import { fetcher } from '#lib/backendFetcher';

const BASE = '/api/v1/agent';

/**
 * The 202 answers of this domain may come without a body; the fetcher would choke on it.
 *
 * @param {string} path
 * @param {object} options
 * @returns {Promise<object|null>}
 */
async function fetchMaybeEmpty(path, options) {
	const res = await fetcher(path, { ...options, responseType: 'stream' });
	const text = await res.text();
	return text ? JSON.parse(text) : null;
}

/**
 * The caller's one unsaved conversation.
 *
 * @returns {Promise<{run: object|null}>}
 */
export const fetchDraft = () => fetcher(`${BASE}/draft`);

/**
 * Creates the draft or returns the existing one.
 *
 * @param {{mandate?: object}} [body]
 * @returns {Promise<{run: object}>}
 */
export const createDraft = (body = {}) => fetcher(`${BASE}/draft`, { method: 'POST', body });

/**
 * "Verwerfen": stops open work, settles holds, the run becomes `cancelled`.
 *
 * @returns {Promise<null>}
 */
export const discardDraft = () => fetcher(`${BASE}/draft`, { method: 'DELETE' });

/**
 * @param {string} runId
 * @returns {Promise<object>} The run snapshot
 */
export const fetchAgentRun = (runId) => fetcher(`${BASE}/runs/${runId}`);

/**
 * Sends a user message; the assistant answers through events.
 *
 * @param {{runId: string, text: string}} params
 * @returns {Promise<{seq: number}>}
 */
export const sendMessage = ({ runId, text }) =>
	fetchMaybeEmpty(`${BASE}/runs/${runId}/messages`, { method: 'POST', body: { text } });

/**
 * The limit chips: persisted at once, a chip change is the user's own confirmation.
 *
 * @param {{runId: string, mandate: object}} params
 * @returns {Promise<{run: object, estimate: object}>}
 */
export const updateMandate = ({ runId, mandate }) =>
	fetcher(`${BASE}/runs/${runId}/mandate`, { method: 'PATCH', body: mandate });

/**
 * The radius chip of a city profile; only while the run is idle. The server posts the change as a
 * user message and starts a new assistant turn.
 *
 * @param {{runId: string, radius: number}} params - radius in km, 1 to 200
 * @returns {Promise<{run: object}>}
 */
export const updateRadius = ({ runId, radius }) =>
	fetcher(`${BASE}/runs/${runId}/radius`, { method: 'PATCH', body: { radius } });

/**
 * "Suche starten" on a plan block.
 *
 * @param {{runId: string, proposalSeq: number, includeUnchecked?: boolean}} params - `includeUnchecked`
 *   only when the plan has companies the qualification could not check; omitted otherwise
 * @returns {Promise<null|object>}
 */
export const confirmPlan = ({ runId, proposalSeq, includeUnchecked }) =>
	fetchMaybeEmpty(`${BASE}/runs/${runId}/confirm`, {
		method: 'POST',
		body: includeUnchecked === undefined ? { proposalSeq } : { proposalSeq, includeUnchecked },
	});

/**
 * @param {string} runId
 * @returns {Promise<null|object>}
 */
export const stopRun = (runId) => fetchMaybeEmpty(`${BASE}/runs/${runId}/stop`, { method: 'POST' });

/**
 * "Speichern & öffnen": renames the run's prospect query and frees the draft slot.
 *
 * @param {{runId: string, name: string}} params
 * @returns {Promise<{prospectQueryId: string, name: string}>}
 */
export const saveRun = ({ runId, name }) => fetcher(`${BASE}/runs/${runId}/save`, { method: 'POST', body: { name } });

/**
 * Whether the popup is open for this user, so a reload restores it.
 *
 * @param {{runId: string, open: boolean}} params
 * @returns {Promise<null>}
 */
export const setPopupOpen = ({ runId, open }) =>
	fetcher(`${BASE}/runs/${runId}/popup`, { method: 'PATCH', body: { open } });

/**
 * @param {string} runId
 * @returns {Promise<object>} The cost ceiling of the current mandate and profile
 */
export const fetchEstimate = (runId) => fetcher(`${BASE}/runs/${runId}/estimate`);

/**
 * Stored events of a conversation; `after` is the last sequence number already known.
 *
 * @param {string} runId
 * @param {{after?: number, limit?: number}} [params]
 * @returns {Promise<{events: Array<{seq: number, type: string, payload: object, createdAt: string}>}>}
 */
export const fetchAgentRunEvents = (runId, { after = 0, limit = 1000 } = {}) =>
	fetcher(`${BASE}/runs/${runId}/events?after=${after}&limit=${limit}`);

/**
 * The up to ten best-fitting companies (name, city, legal form), free before purchase.
 *
 * @param {string} runId
 * @returns {Promise<{companies: Array<{companyId: string, name: string, city: string|null, legalForm: string|null}>}>}
 */
export const fetchAgentRunPreview = (runId) => fetcher(`${BASE}/runs/${runId}/preview`);

/**
 * The audit ("Protokoll") of a conversation: tool calls with input, status, credits, cost.
 *
 * @param {string} runId
 * @returns {Promise<{actions: Array<object>}>}
 */
export const fetchAgentRunActions = (runId) => fetcher(`${BASE}/runs/${runId}/actions`);

/**
 * @param {string} runId
 * @param {number} after - Last known sequence number; the stream replays everything after it
 * @returns {string} Absolute SSE URL
 */
export const agentRunStreamUrl = (runId, after = 0) =>
	`${process.env.REACT_APP_BACKEND_WORKER_URL}${BASE}/runs/${runId}/events/stream?after=${after}`;
